import React, { useState, useEffect } from 'react';
import './UserManagement.css'; // Import specific CSS for this module
import { authApi } from '../services/api';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [newUser, setNewUser] = useState({ name: '', email: '', role: '', password: '' });
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const roles = ['admin', 'staff'];

    // Fetch users on component mount
    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setIsLoading(true);
            const response = await authApi.getUsers();
            setUsers(response.data.data.users);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch users. Please try again.');
            console.error('Error fetching users:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const validateForm = () => {
        if (!newUser.name.trim()) {
            setError('Name is required');
            return false;
        }
        if (!newUser.email.trim()) {
            setError('Email is required');
            return false;
        }
        if (!newUser.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            setError('Please enter a valid email address');
            return false;
        }
        if (!newUser.password.trim()) {
            setError('Password is required');
            return false;
        }
        if (newUser.password.length < 6) {
            setError('Password must be at least 6 characters long');
            return false;
        }
        if (!newUser.role) {
            setError('Please select a role');
            return false;
        }
        return true;
    };

    const addUser = async () => {
        try {
            setError('');
            if (!validateForm()) return;

            setIsLoading(true);
            const response = await authApi.register(newUser);
            setUsers([...users, response.data.data.user]);
            setNewUser({ name: '', email: '', role: '', password: '' });
            setSuccessMessage('User added successfully!');
            
            // Refresh the users list
            await fetchUsers();
            
            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add user. Please try again.');
            console.error('Error adding user:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteUser = async (userId) => {
        try {
            setIsLoading(true);
            await authApi.deleteUser(userId);
            // Refresh the users list
            await fetchUsers();
            setSuccessMessage('User deleted successfully!');
            
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete user. Please try again.');
            console.error('Error deleting user:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="user-management-container">
            <h1>User Management</h1>
            
            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}
            
            <div className="user-form">
                <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input 
                        id="name"
                        type="text" 
                        className="form-control" 
                        placeholder="Enter full name" 
                        value={newUser.name} 
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} 
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input 
                        id="email"
                        type="email" 
                        className="form-control" 
                        placeholder="Enter email" 
                        value={newUser.email} 
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} 
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input 
                        id="password"
                        type="password" 
                        className="form-control" 
                        placeholder="Enter password" 
                        value={newUser.password} 
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} 
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="role">Role</label>
                    <select 
                        id="role"
                        className="form-control" 
                        value={newUser.role} 
                        onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    >
                        <option value="">Select Role</option>
                        {roles.map(role => (
                            <option key={role} value={role}>
                                {role.charAt(0).toUpperCase() + role.slice(1)}
                            </option>
                        ))}
                    </select>
                </div>

                <button 
                    className="btn-add-user" 
                    onClick={addUser}
                    disabled={isLoading}
                >
                    {isLoading ? 'Adding...' : 'Add User'}
                </button>
            </div>

            <h3>User List</h3>
            <div className="user-list">
                {isLoading && <p className="loading">Loading users...</p>}
                {!isLoading && users.length === 0 ? (
                    <p className="no-users">No users added yet</p>
                ) : (
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.id} className="user-item">
                                    <td>{user.name}</td>
                                    <td>{user.email}</td>
                                    <td>{user.role.charAt(0).toUpperCase() + user.role.slice(1)}</td>
                                    <td>
                                        <button 
                                            className="btn-delete-user" 
                                            onClick={() => deleteUser(user.id)}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Deleting...' : 'Delete'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default UserManagement; 